/**
 * MobileNav — app-style top bar for phones only.
 *
 * Left: hamburger → slide-out nav with module links
 * Right: avatar circle → links to /profile
 *
 * Rendered on every page via App.jsx but hidden on desktop via CSS.
 * The slide-out menu lists modules the user can access.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext.jsx';
import './MobileNav.css';


const NAV_ITEMS = [
  { to: '/',       label: 'Home' },
  { to: '/tracks', label: 'Tracks' },
  { to: '/quotes', label: 'Quotes' },
  { to: '/funny',  label: 'Funny Page' },
  { to: '/launches', label: 'Launches' },
  { href: 'https://photos.nachofiles.com',    label: 'Photos',    external: true },
  { href: 'https://nextcloud.nachofiles.com',  label: 'Nextcloud', external: true },
  { href: 'https://immich.nachofiles.com',     label: 'Immich',    external: true },
];


export default function MobileNav() {
  const { user } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Don't render on admin pages — AdminLayout has its own mobile menu
  if (
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/accept-invite') ||
    location.pathname.startsWith('/request-invite')
  ) return null;

  const initials = user
    ? user.display_name.slice(0, 2).toUpperCase()
    : '?';

  return (
    <>
      <header className="mnav-bar">
        <button
          className="mnav-hamburger"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="currentColor">
            <rect y="4" width="22" height="2.2" rx="1" />
            <rect y="10" width="22" height="2.2" rx="1" />
            <rect y="16" width="22" height="2.2" rx="1" />
          </svg>
        </button>

        {user ? (
          <Link to="/profile" className="mnav-avatar">
          {user.avatar_url
            ? <img src={`${user.avatar_url}?v=${Date.now()}`} alt="" className="mnav-avatar-img" />
            : initials
          }
          </Link>
        ) : (
          <Link to="/login" className="mnav-signin">sign in</Link>
        )}
      </header>

      {/* Slide-out menu */}
      {menuOpen && (
        <div className="mnav-backdrop" onClick={() => setMenuOpen(false)} />
      )}

      <nav className={`mnav-drawer ${menuOpen ? 'open' : ''}`}>
        <div className="mnav-drawer-top">
          <img
            src="/nachofiles-wordmark-small.png"
            alt="NachoFiles"
            className="mnav-drawer-logo"
          />
          <button
            className="mnav-drawer-close"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        <div className="mnav-drawer-items">
          {NAV_ITEMS.map((item) =>
            item.external ? (
              <a
                key={item.href}
                href={item.href}
                className="mnav-drawer-item"
                target="_blank"
                rel="noopener"
              >
                <span>{item.label}</span>
                <span className="mnav-external-icon">↗</span>
              </a>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className={`mnav-drawer-item ${location.pathname === item.to ? 'active' : ''}`}
              >
                {item.label}
              </Link>
            )
          )}

          {user?.role === 'admin' && (
            <>
              <div className="mnav-drawer-divider" />
              <Link
                to="/admin"
                className={`mnav-drawer-item mnav-admin-item ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
              >
                Admin
              </Link>
            </>
          )}
        </div>

        {user && (
          <div className="mnav-drawer-user">
            <div className="mnav-drawer-avatar">{initials}</div>
            <div>
              <div className="mnav-drawer-name">{user.display_name}</div>
              <div className="mnav-drawer-meta">@{user.username}</div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
